import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Globe2,
  UserCheck,
  Target,
  Send,
  ClipboardList,
  TrendingUp,
  Sparkles,
  RefreshCw,
  BarChart3,
} from 'lucide-react';
import GrowthTabNav from './GrowthTabNav';
import GrowthDateFilter from './GrowthDateFilter';
import { getGrowthOverview } from '../../Services/GrowthService';
import { GrowthOverview } from '../../Types/GrowthType';

const StatCard = ({
  label,
  value,
  icon,
  gradient,
  sub,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  gradient: string;
  sub?: string;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35 }}
    className={`relative overflow-hidden rounded-2xl p-5 text-white shadow-lg ${gradient}`}
  >
    <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white/10 blur-2xl" />
    <div className="relative z-10 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="rounded-lg bg-white/20 p-2 backdrop-blur-md">{icon}</span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-white/80">Growth</span>
      </div>
      <div>
        <h4 className="text-3xl font-black">{value.toLocaleString()}</h4>
        <p className="text-sm font-bold opacity-90">{label}</p>
      </div>
      {sub && <p className="text-[10px] font-medium text-white/70 italic">{sub}</p>}
    </div>
  </motion.div>
);

const MiniStat = ({ label, value, icon }: { label: string; value: string | number; icon: React.ReactNode }) => (
  <div className="flex items-center justify-between rounded-xl bg-slate-950/60 px-4 py-3">
    <span className="flex items-center gap-2 text-xs font-semibold text-slate-400">
      {icon}
      {label}
    </span>
    <span className="text-sm font-black text-slate-100">{value.toLocaleString()}</span>
  </div>
);

const SectionCard = ({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
    <div className="mb-5 flex items-center gap-3">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
        {icon}
      </span>
      <h3 className="text-base font-bold text-slate-100">{title}</h3>
    </div>
    {children}
  </div>
);

function GrowthOverviewComponent() {
  const [data, setData] = useState<GrowthOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');
  const [role, setRole] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getGrowthOverview({
        fromDate: appliedFrom || undefined,
        toDate: appliedTo || undefined,
        role: role || undefined,
      });
      if (response.success && response.data) {
        setData(response.data);
      }
    } catch (err) {
      console.error('Failed to load growth overview', err);
    } finally {
      setLoading(false);
    }
  }, [appliedFrom, appliedTo, role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sourceBars = useMemo(() => {
    if (!data) return [];
    return [
      { label: 'LinkedIn', value: data.linkedInVisitors },
      { label: 'Organic', value: data.organicSearchVisitors },
      { label: 'Community', value: data.communityVisitors },
      { label: 'Referral', value: data.referralVisitors },
    ];
  }, [data]);

  const maxBar = useMemo(
    () => Math.max(1, ...(data?.growthSeries || []), ...sourceBars.map((s) => s.value)),
    [data, sourceBars]
  );

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-slate-950 p-4 md:p-8">
        <div className="flex min-h-[60vh] items-center justify-center text-slate-400">
          <RefreshCw className="h-10 w-10 animate-spin text-cyan-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <GrowthTabNav />

      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
          <BarChart3 size={22} />
        </span>
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Growth Overview</h1>
          <p className="text-xs font-semibold text-slate-400 md:text-sm">
            Candidate acquisition, visitor tracking &amp; invitation health
          </p>
        </div>
        <button
          onClick={fetchData}
          title="Refresh"
          className="ml-auto inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-3 xl:grid-cols-2">
        <GrowthDateFilter
          fromDate={fromDate}
          toDate={toDate}
          onApply={(f, t) => {
            setFromDate(f);
            setToDate(t);
            setAppliedFrom(f);
            setAppliedTo(t);
          }}
          extra={
            <div className="flex items-center gap-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Role</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs font-semibold text-slate-100 outline-none focus:border-cyan-500/50 [color-scheme:dark]">
                <option value="">All roles</option>
                <option value="CANDIDATE">Candidate</option>
                <option value="RECRUITER">Recruiter</option>
              </select>
            </div>
          }
        />
      </div>

      {!data ? (
        <div className="flex flex-col items-center gap-4 rounded-2xl border border-slate-800 bg-slate-900/60 p-16 text-center">
          <p className="text-lg font-bold text-slate-500">Unable to load growth data</p>
          <button onClick={fetchData} className="rounded-xl bg-cyan-500 px-5 py-2.5 text-sm font-black text-slate-950 hover:bg-cyan-400">
            Retry
          </button>
        </div>
      ) : (
        <>
          {/* KPI row */}
          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Total Visitors"
              value={data.totalVisitors}
              icon={<Globe2 className="h-5 w-5" />}
              gradient="bg-gradient-to-br from-blue-600 to-indigo-700"
              sub={`${data.uniqueVisitors} unique`}
            />
            <StatCard
              label="Registrations Started"
              value={data.registrationStarted}
              icon={<ClipboardList className="h-5 w-5" />}
              gradient="bg-gradient-to-br from-fuchsia-500 to-purple-700"
            />
            <StatCard
              label="Registrations Completed"
              value={data.registrationCompleted}
              icon={<UserCheck className="h-5 w-5" />}
              gradient="bg-gradient-to-br from-emerald-500 to-teal-700"
              sub={`${data.registrationConversionPct.toFixed(1)}% conversion`}
            />
            <StatCard
              label="Approved Candidates"
              value={data.approvedCandidates}
              icon={<Target className="h-5 w-5" />}
              gradient="bg-gradient-to-br from-amber-500 to-orange-600"
              sub={`${data.approvalPct.toFixed(1)}% approval`}
            />
          </div>

          <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <SectionCard title="Visitor Sources" icon={<Globe2 size={18} />}>
              <div className="flex flex-col gap-2.5">
                {sourceBars.map((s) => (
                  <div key={s.label} className="flex items-center gap-3">
                    <span className="w-24 truncate text-right text-xs font-semibold text-slate-400">{s.label}</span>
                    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-800">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 transition-all duration-700"
                        style={{ width: `${Math.max(2, (s.value / maxBar) * 100)}%` }}
                      />
                    </div>
                    <span className="w-10 text-xs font-bold text-slate-200">{s.value}</span>
                  </div>
                ))}
                {sourceBars.every((s) => s.value === 0) && (
                  <p className="text-sm text-slate-500">No visitor source data yet.</p>
                )}
              </div>
            </SectionCard>

            <SectionCard title="Cumulative Growth" icon={<TrendingUp size={18} />}>
              {data.growthSeries.length > 0 ? (
                <div className="flex h-40 items-end gap-1">
                  {data.growthSeries.map((value, idx) => (
                    <div
                      key={idx}
                      title={`${value}`}
                      className="flex-1 rounded-t-md bg-gradient-to-t from-cyan-600 to-teal-400 transition-all duration-500"
                      style={{ height: `${Math.max(3, (value / Math.max(1, ...data.growthSeries)) * 100)}%` }}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No growth series data for this range.</p>
              )}
              <p className="mt-3 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                {data.totalCandidates} candidates in total
              </p>
            </SectionCard>

            <SectionCard title="Acquisition Health" icon={<Sparkles size={18} />}>
              <div className="flex flex-col gap-2.5">
                <MiniStat label="Referral Conversion" value={`${data.referralConversionPct.toFixed(1)}%`} icon={<Users size={14} />} />
                <MiniStat label="Total Prospects" value={data.totalProspects} icon={<ClipboardList size={14} />} />
                <MiniStat label="Invitations Sent" value={data.invitationsSent} icon={<Send size={14} />} />
                <MiniStat label="Referral Visitors" value={data.referralVisitors} icon={<Users size={14} />} />
              </div>
            </SectionCard>
          </div>
        </>
      )}
    </div>
  );
}

export default GrowthOverviewComponent;